const AV_SEARCH_DEBOUNCE_MS = 170;
const AV_MIN_QUERY_LENGTH = 2;
const AV_PRODUCT_LIMIT = 5;
const AV_COLLECTION_LIMIT = 3;
const AV_QUERY_LIMIT = 3;

class ArcaneHeaderSearch extends HTMLElement {
  constructor() {
    super();

    this.abortController = null;
    this.debounceTimer = null;
    this.portal = null;
    this.initialized = false;

    this.onInput = this.onInput.bind(this);
    this.onReset = this.onReset.bind(this);
    this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onViewportChange = this.onViewportChange.bind(this);
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.input = this.querySelector('[data-av-search-input]');
    this.resetButton = this.querySelector('[data-av-search-reset]');
    this.anchor = this.querySelector('[data-av-search-anchor]');
    this.form = this.querySelector('[data-av-search-form]');

    if (!this.input || !this.anchor || !this.form) return;

    this.createPortal();

    this.input.addEventListener('input', this.onInput);
    this.input.addEventListener('keydown', this.onKeyDown);
    this.resetButton?.addEventListener('click', this.onReset);

    document.addEventListener('pointerdown', this.onDocumentPointerDown);
    window.addEventListener('resize', this.onViewportChange, { passive: true });
    window.addEventListener('scroll', this.onViewportChange, { passive: true, capture: true });
  }

  disconnectedCallback() {
    clearTimeout(this.debounceTimer);
    this.abortController?.abort();

    this.input?.removeEventListener('input', this.onInput);
    this.input?.removeEventListener('keydown', this.onKeyDown);
    this.resetButton?.removeEventListener('click', this.onReset);

    document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    window.removeEventListener('resize', this.onViewportChange);
    window.removeEventListener('scroll', this.onViewportChange, true);

    this.portal?.remove();
    this.portal = null;
    this.initialized = false;
  }

  createPortal() {
    const portal = document.createElement('div');
    portal.className = 'av-header-search-portal';
    portal.dataset.open = 'false';
    portal.setAttribute('role', 'listbox');
    portal.setAttribute('aria-label', 'Search suggestions');
    portal.dir = this.dataset.direction || document.documentElement.dir || 'ltr';

    const computed = getComputedStyle(this);
    [
      '--av-search-background',
      '--av-search-text-color',
      '--av-search-border-width',
      '--av-search-border-color',
      '--av-search-dropdown-radius',
      '--av-search-dropdown-max-height',
    ].forEach((name) => {
      const value = computed.getPropertyValue(name);
      if (value) portal.style.setProperty(name, value);
    });

    document.body.appendChild(portal);
    this.portal = portal;
  }

  onInput() {
    const query = this.input.value.trim();
    this.syncResetButton(query);

    clearTimeout(this.debounceTimer);
    this.abortController?.abort();

    if (query.length < AV_MIN_QUERY_LENGTH) {
      this.close();
      return;
    }

    this.debounceTimer = window.setTimeout(() => {
      this.fetchResults(query);
    }, AV_SEARCH_DEBOUNCE_MS);
  }

  onReset() {
    this.abortController?.abort();
    clearTimeout(this.debounceTimer);

    this.input.value = '';
    this.syncResetButton('');
    this.close();
    this.input.focus();
  }

  onDocumentPointerDown(event) {
    if (!this.portal) return;
    const target = event.target;

    if (target instanceof Node && (this.contains(target) || this.portal.contains(target))) {
      return;
    }

    this.close();
  }

  onKeyDown(event) {
    if (event.key === 'Escape') {
      this.close();
      return;
    }

    if (event.key === 'ArrowDown' && this.portal?.dataset.open === 'true') {
      const firstLink = this.portal.querySelector('a[href]');
      if (firstLink instanceof HTMLElement) {
        event.preventDefault();
        firstLink.focus();
      }
    }
  }

  onViewportChange() {
    if (this.portal?.dataset.open === 'true') {
      this.positionPortal();
    }
  }

  syncResetButton(query) {
    if (!this.resetButton) return;
    this.resetButton.hidden = query.length === 0;
  }

  buildPredictiveUrl(query) {
    const raw = this.dataset.predictiveUrl || `${window.Shopify?.routes?.root || '/'}search/suggest`;
    const url = new URL(raw, window.location.origin);

    if (!url.pathname.endsWith('.json')) {
      url.pathname = `${url.pathname.replace(/\/$/, '')}.json`;
    }

    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'query,product,collection');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'last');

    return url;
  }

  async fetchResults(query) {
    this.abortController = new AbortController();

    // Shopify's Ajax Predictive Search API does not currently support Arabic
    // buyer locales. Use Shopify's normal storefront search + Section Rendering
    // API for Arabic so translated product titles remain searchable.
    if (this.isArabicLocale()) {
      await this.fetchRenderedSearchResults(query);
      return;
    }

    try {
      const response = await fetch(this.buildPredictiveUrl(query), {
        signal: this.abortController.signal,
        headers: {
          Accept: 'application/json',
        },
      });

      // 417 = unsupported buyer locale. Fall back to normal storefront search
      // rather than showing an error. This also makes the component robust for
      // any future locale that Shopify's predictive endpoint doesn't support.
      if (response.status === 417) {
        await this.fetchRenderedSearchResults(query);
        return;
      }

      if (!response.ok) {
        throw new Error(`Predictive search returned ${response.status}`);
      }

      const payload = await response.json();

      // Ignore stale responses if the visitor has already typed something else.
      if (query !== this.input.value.trim()) return;

      const results = payload?.resources?.results || {};
      this.renderResults(query, results);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('[Arcane Vault search]', error);
      this.renderError();
    }
  }

  isArabicLocale() {
    return (this.dataset.locale || document.documentElement.lang || '')
      .toLowerCase()
      .startsWith('ar');
  }

  buildRenderedSearchUrl(query) {
    const raw = this.dataset.searchUrl || `${window.Shopify?.routes?.root || '/'}search`;
    const url = new URL(raw, window.location.origin);

    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    url.searchParams.set('section_id', 'av-header-search-results');

    return url;
  }

  async fetchRenderedSearchResults(query) {
    try {
      const response = await fetch(this.buildRenderedSearchUrl(query), {
        signal: this.abortController.signal,
        headers: {
          Accept: 'text/html',
        },
      });

      if (!response.ok) {
        throw new Error(`Rendered search returned ${response.status}`);
      }

      const html = await response.text();

      // Ignore stale responses if the visitor has already typed something else.
      if (query !== this.input.value.trim()) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const rendered = doc.querySelector('[data-av-server-search-results]');

      if (!rendered) {
        throw new Error('Rendered search response did not contain Arcane results markup');
      }

      this.renderServerResults(query, rendered);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('[Arcane Vault Arabic search]', error);
      this.renderError();
    }
  }

  renderServerResults(query, rendered) {
    if (!this.portal) return;

    this.portal.replaceChildren();

    const scroll = document.createElement('div');
    scroll.className = 'av-header-search-portal__scroll';

    for (const child of rendered.children) {
      scroll.appendChild(child.cloneNode(true));
    }

    const footer = document.createElement('div');
    footer.className = 'av-header-search-portal__footer';

    const viewAll = document.createElement('a');
    viewAll.className = 'av-header-search-portal__view-all';
    viewAll.href = this.buildSearchUrl(query);
    viewAll.textContent = this.dataset.viewAllLabel || 'View all results';
    footer.appendChild(viewAll);

    this.portal.append(scroll, footer);
    this.open();
  }

  renderResults(query, results) {
    if (!this.portal) return;

    this.portal.replaceChildren();

    const scroll = document.createElement('div');
    scroll.className = 'av-header-search-portal__scroll';

    const queries = Array.isArray(results.queries) ? results.queries.slice(0, AV_QUERY_LIMIT) : [];
    const products = Array.isArray(results.products) ? results.products.slice(0, AV_PRODUCT_LIMIT) : [];
    const collections = Array.isArray(results.collections)
      ? results.collections.slice(0, AV_COLLECTION_LIMIT)
      : [];

    if (queries.length) {
      scroll.appendChild(
        this.createSection(this.dataset.suggestionsLabel || 'Suggestions', (section) => {
          const list = document.createElement('div');
          list.className = 'av-header-search-portal__queries';

          queries.forEach((item) => {
            const link = document.createElement('a');
            link.className = 'av-header-search-portal__query';
            link.href = item.url;
            link.textContent = item.text || query;
            list.appendChild(link);
          });

          section.appendChild(list);
        })
      );
    }

    if (products.length) {
      scroll.appendChild(
        this.createSection(this.dataset.productsLabel || 'Products', (section) => {
          const list = document.createElement('div');
          list.className = 'av-header-search-portal__products';

          products.forEach((product) => {
            list.appendChild(this.createProduct(product));
          });

          section.appendChild(list);
        })
      );
    }

    if (collections.length) {
      scroll.appendChild(
        this.createSection(this.dataset.collectionsLabel || 'Collections', (section) => {
          const list = document.createElement('div');
          list.className = 'av-header-search-portal__collections';

          collections.forEach((collection) => {
            const link = document.createElement('a');
            link.className = 'av-header-search-portal__collection';
            link.href = collection.url;
            link.textContent = collection.title || '';
            list.appendChild(link);
          });

          section.appendChild(list);
        })
      );
    }

    if (!queries.length && !products.length && !collections.length) {
      const empty = document.createElement('div');
      empty.className = 'av-header-search-portal__empty';
      empty.textContent = this.dataset.noResultsLabel || 'No results found';
      scroll.appendChild(empty);
    }

    const footer = document.createElement('div');
    footer.className = 'av-header-search-portal__footer';

    const viewAll = document.createElement('a');
    viewAll.className = 'av-header-search-portal__view-all';
    viewAll.href = this.buildSearchUrl(query);
    viewAll.textContent = this.dataset.viewAllLabel || 'View all results';
    footer.appendChild(viewAll);

    this.portal.append(scroll, footer);
    this.open();
  }

  renderError() {
    if (!this.portal) return;

    this.portal.replaceChildren();

    const error = document.createElement('div');
    error.className = 'av-header-search-portal__error';
    error.textContent = this.dataset.errorLabel || 'Could not load suggestions. Press Enter to search.';

    this.portal.appendChild(error);
    this.open();
  }

  createSection(title, populate) {
    const section = document.createElement('section');
    section.className = 'av-header-search-portal__section';

    const heading = document.createElement('h3');
    heading.className = 'av-header-search-portal__heading';
    heading.textContent = title;
    section.appendChild(heading);

    populate(section);
    return section;
  }

  createProduct(product) {
    const link = document.createElement('a');
    link.className = 'av-header-search-portal__product';
    link.href = product.url;

    const imageWrap = document.createElement('div');
    imageWrap.className = 'av-header-search-portal__image-wrap';

    const imageUrl = product?.featured_image?.url || product?.image || '';
    if (imageUrl) {
      const img = document.createElement('img');
      img.className = 'av-header-search-portal__image';
      img.src = imageUrl;
      img.alt = product?.featured_image?.alt || product.title || '';
      img.loading = 'lazy';
      imageWrap.appendChild(img);
    }

    const copy = document.createElement('div');
    copy.className = 'av-header-search-portal__product-copy';

    const title = document.createElement('div');
    title.className = 'av-header-search-portal__product-title';
    title.textContent = product.title || '';
    copy.appendChild(title);

    const metaParts = [];
    if (product.vendor) metaParts.push(product.vendor);

    const price = this.formatPrice(product.price);
    if (price) metaParts.push(price);

    if (metaParts.length) {
      const meta = document.createElement('div');
      meta.className = 'av-header-search-portal__product-meta';
      meta.textContent = metaParts.join(' · ');
      copy.appendChild(meta);
    }

    link.append(imageWrap, copy);
    return link;
  }

  formatPrice(value) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '';

    const locale = this.dataset.locale || document.documentElement.lang || 'en';
    const currency = this.dataset.currency || 'SAR';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        maximumFractionDigits: 2,
      }).format(numeric);
    } catch {
      return `${numeric.toFixed(2)} ${currency}`;
    }
  }

  buildSearchUrl(query) {
    const raw = this.dataset.searchUrl || `${window.Shopify?.routes?.root || '/'}search`;
    const url = new URL(raw, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('options[prefix]', 'last');
    return url.toString();
  }

  positionPortal() {
    if (!this.portal || !this.anchor) return;

    const rect = this.anchor.getBoundingClientRect();
    const viewportPadding = 8;
    const availableWidth = Math.max(0, window.innerWidth - viewportPadding * 2);

    const width = Math.min(rect.width, availableWidth);
    let left = rect.left;

    if (left + width > window.innerWidth - viewportPadding) {
      left = window.innerWidth - viewportPadding - width;
    }

    left = Math.max(viewportPadding, left);

    this.portal.style.left = `${Math.round(left)}px`;
    this.portal.style.top = `${Math.round(rect.bottom + 8)}px`;
    this.portal.style.width = `${Math.round(width)}px`;
  }

  open() {
    if (!this.portal) return;

    this.positionPortal();
    this.portal.dataset.open = 'true';
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    if (!this.portal) return;

    this.portal.dataset.open = 'false';
    this.input?.setAttribute('aria-expanded', 'false');
  }
}

if (!customElements.get('av-header-search')) {
  customElements.define('av-header-search', ArcaneHeaderSearch);
}
